import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../../api/axiosConfig";
import { FiMail, FiLock, FiShield } from "react-icons/fi";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Kullanıcı yazmaya devam ederse error'u temizle
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    // Cache version control - production deployment fix
    try {
      const currentBuildTime = Date.now().toString();
      const lastBuildTime = localStorage.getItem('app-build-time');

      // Check if this is a new deployment (24 hours difference)
      if (lastBuildTime && (currentBuildTime - parseInt(lastBuildTime)) > 86400000) {
        console.log('Detected new deployment, clearing cache');
        // Clear all auth-related cache
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminRole');
        localStorage.removeItem('adminId');
        localStorage.removeItem('adminName');
        localStorage.removeItem('menuPermissions');
      }

      localStorage.setItem('app-build-time', currentBuildTime);
    } catch (error) {
      console.log('Cache check failed:', error);
    }

    console.log("=== FRONTEND LOGIN ATTEMPT ===");
    console.log("Email:", formData.email);
    console.log("Time:", new Date().toISOString());

    try {
      const response = await axiosConfig.post("/admin/all/login", {
        email: formData.email,
        password: formData.password
      });

      console.log("Login response received:", response.data);

      const { token, refreshToken, role, adminId, success, message, menuPermissions, adminName } = response.data;

      if (!token) {
        console.error("NO TOKEN IN RESPONSE!");
        setErrorMessage("Token oluşturulamadı!");
        setLoading(false);
        return;
      }

      console.log("Token received:", token.substring(0, 50) + "...");
      console.log("Role:", role);
      console.log("AdminId:", adminId);
      console.log("Success:", success);

      // LocalStorage'a kaydet
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken); // Refresh token'ı kaydet
      }
      localStorage.setItem("adminRole", role);
      localStorage.setItem("adminId", adminId);
      localStorage.setItem("adminName", adminName || formData.email.split('@')[0]); // Admin adını kaydet

      // Axios header'ı hemen güncelle
      axiosConfig.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Menü yetkilerini kaydet
      if (menuPermissions && menuPermissions.length > 0) {
        localStorage.setItem("menuPermissions", JSON.stringify(menuPermissions));
      } else {
        localStorage.setItem("menuPermissions", JSON.stringify([]));
      }

      // Verify localStorage
      console.log("=== LOCALSTORAGE CHECK ===");
      console.log("Stored token:", localStorage.getItem("token")?.substring(0, 50) + "...");
      console.log("Stored role:", localStorage.getItem("adminRole"));
      console.log("Stored adminId:", localStorage.getItem("adminId"));
      console.log("Stored menuPermissions:", localStorage.getItem("menuPermissions"));

      console.log("=== LOGIN SUCCESS - REDIRECTING ===");

      // Başarılı giriş
      navigate("/admin");
    } catch (error) {
      console.error("=== LOGIN ERROR ===");
      console.error("Error object:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);

      if (error.response) {
        setErrorMessage(error.response.data.message || "Login başarısız!");
        console.error("Server error message:", error.response.data.message);
      } else {
        setErrorMessage("Sunucuya bağlanılamadı!");
        console.error("Network error or server unreachable");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-pink-500/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-4xl w-full grid lg:grid-cols-2 gap-8 items-center">

          {/* Left Side - Welcome Section */}
          <div className="text-white space-y-8 lg:pr-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="text-6xl">🛡️</div>
                <div>
                  <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                    Admin Control
                  </h1>
                  <p className="text-purple-200 text-xl">Sistem Yönetim Merkezi</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center text-2xl">
                  ⚡
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Güçlü Yönetim</h3>
                  <p className="text-purple-200">Tam sistem kontrolü</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-2xl">
                  📊
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Detaylı Analiz</h3>
                  <p className="text-purple-200">Gerçek zamanlı raporlar</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-2xl">
                  🔒
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Maksimum Güvenlik</h3>
                  <p className="text-purple-200">Çok katmanlı koruma</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex justify-between items-center text-center">
                <div>
                  <div className="text-3xl font-bold text-yellow-400">99.9%</div>
                  <div className="text-purple-200 text-sm">Uptime</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-400">24/7</div>
                  <div className="text-purple-200 text-sm">Monitoring</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-400">∞</div>
                  <div className="text-purple-200 text-sm">Güç</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <div className="text-4xl mb-4">👑</div>
                <h2 className="text-3xl font-bold text-white mb-2">Hoş Geldiniz</h2>
                <p className="text-purple-200">Yönetici bilgilerinizi girin</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {errorMessage && (
                  <div className="bg-red-500/20 backdrop-blur border border-red-400/50 text-red-100 px-4 py-3 rounded-xl">
                    <span className="block sm:inline">{errorMessage}</span>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    📧 E-posta Adresi
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                      placeholder="admin@example.com"
                      disabled={loading}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">✨</div>
                    </div>
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    🔐 Şifre
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/10 backdrop-blur border border-white/30 rounded-xl text-white placeholder-white/60 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <div className="text-white/40">🔥</div>
                    </div>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center text-white/80">
                    <input
                      type="checkbox"
                      className="mr-2 rounded bg-white/10 border-white/30 text-yellow-400 focus:ring-yellow-400"
                    />
                    Beni hatırla
                  </label>
                  <a href="#" className="text-yellow-400 hover:text-yellow-300 font-medium">
                    Şifremi unuttum?
                  </a>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-4 rounded-xl text-white font-bold text-lg transition-all transform hover:scale-105 ${
                    loading
                      ? 'bg-gray-500/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-500 hover:via-pink-600 hover:to-purple-700 shadow-lg'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                      Giriş yapılıyor...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">🚀</span>
                      Admin Paneline Gir
                    </div>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-white/60 text-sm">
                  🔰 Güvenli ve şifreli bağlantı
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;